import React, { useCallback, useMemo } from 'react';
import moment from 'moment';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Button, connectModal } from '@gsa/afp-component-library';
import { useEditSolicitation } from '../../components/apis/edit-solicitation-provider';
import useReopenPeriod from './reopen-period-apis';

import SolicitationPeriod from '../../components/solicitation-period/solicitation-period';
import SolicitationNotification from '../../utils/solicitation-notification';
import SolicitationHeader from '../../components/solicitation-header';
import SolicitationStepper from '../../components/solicitation-stepper';
import { reopenPeriodFieldProps } from '../../constants';
import { POSSIBLE_DATE_FORMAT } from '../../components/solicitation-form-context';
import ReopenPeriodsTable from './reopen-periods-table';

import { getTimestampFromDateNTime } from '../../utils/open-reopen-period-util';
import EditReopenPeriodModal from './edit-reopen-period-modal';
import useReopenPeriodsRowActions from './use-reopen-periods-row-actions';
import solicitationNotifications from '../../utils/solicitation-notification-helpers';
import DeleteReopenPeriodModal from './delete-reopen-period-modal';
import { vendorIds } from '../../atoms/solicitation-helpers';
import { sortDateAscendingWithAccessors } from '@/utilities/date-helpers';
import { getSolicitationPeriodMinDate } from '../../utils/open-periods-helpers';
import InactiveSIAlert from '../../components/solicitation-alert/inactive-si-alert';
import { getAddReopenPeriodYupSchema } from './reopen-period-yup-schema';

export const parseDateString = (value, originalValue) => {
  if (!originalValue) return null;
  const parsedDate =
    originalValue instanceof Date && originalValue
      ? originalValue
      : moment(originalValue, POSSIBLE_DATE_FORMAT, true).toDate();
  return parsedDate;
};

const ConnectedDeleteReopenPeriodModal = connectModal(DeleteReopenPeriodModal);

const ReopenPeriods = () => {
  const navigate = useNavigate();
  const setSolicitationNotification = useSetRecoilState(
    solicitationNotifications,
  );

  const {
    solicitation,
    solicitationID,
    openPeriod,
    reopenPeriods,
    refetchSolicitation,
  } = useEditSolicitation();

  const { addSolicitationPeriod, addPeriodLoading } =
    useReopenPeriod(refetchSolicitation);

  const reopenPeriodListDescendingOrder = [...reopenPeriods]?.reverse();

  const newReopenPeriodMinTimestamp =
    reopenPeriodListDescendingOrder?.[0]?.endDate || openPeriod?.endDate;

  const newReopenPeriodMinDateFormated = newReopenPeriodMinTimestamp
    ? moment(newReopenPeriodMinTimestamp).format('MM/DD/YYYY')
    : getSolicitationPeriodMinDate(solicitation.contractYear);

  const onClickPrev = () => {
    setSolicitationNotification([]);
    navigate(
      `/catalog/solicitations/edit/${Number(solicitationID)}/open-period`,
    );
  };
  const onClickNext = () => {
    setSolicitationNotification([]);
    navigate(
      `/catalog/solicitations/edit/${Number(solicitationID)}/select-sin`,
    );
  };

  const schema = getAddReopenPeriodYupSchema({
    openPeriod,
    reopenPeriodListDescendingOrder,
    newReopenPeriodMinTimestamp,
    newReopenPeriodMinDateFormated,
    solicitation,
  });

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      audience: 'Open',
      startTime: '',
      endTime: '',
      startDate: '',
      endDate: '',
    },
  }) || {};

  const {
    showEditReopenPeriodModal,
    currentPeriodModalData,
    toggleEditReopenPeriodModal,
    showDeleteReopenPeriodModal,
    toggleDeleteReopenPeriodModal,
    handleRowActionEdit,
    handleRowActionDelete,
  } = useReopenPeriodsRowActions();

  const [selectedVendorIds] = useRecoilState(vendorIds);

  const idsArr = useMemo(() => {
    if (selectedVendorIds?.length > 0) {
      return selectedVendorIds;
    }
    const ids = currentPeriodModalData?.solicitationPeriodVendors?.map(
      (vendor) => ({
        vendorId: vendor?.vendorId,
        audienceType: vendor?.audienceType,
      }),
    );
    return ids;
  }, [selectedVendorIds, currentPeriodModalData]);

  const submitHandler = (data) => {
    const { startDate, endDate, startTime, endTime, audience } = data || {};

    if (openPeriod.startDate && openPeriod.endDate) {
      if (startDate && endDate && startTime && endTime) {
        addSolicitationPeriod({
          variables: {
            solicitationPeriodInput: {
              solicitationId: Number(solicitationID),
              startDate: getTimestampFromDateNTime(startDate, startTime),
              endDate: getTimestampFromDateNTime(endDate, endTime),
              audience,
              vendorIds: idsArr,
            },
          },
        });
        reset();
      }
    } else {
      setSolicitationNotification([
        {
          id: 'ADD_OR_UPDATE_SOLICITATION_PERIOD_ERROR',
          message:
            'Reopen or mid-cycle periods cannot be entered without an Open period.',
          type: 'error',
          closeable: false,
          showInModal: false,
        },
      ]);
    }
  };

  const getContractAwardDate = useCallback(
    () =>
      solicitation?.contracts
        ?.filter(
          (aContract) => aContract?.contractYear === solicitation?.contractYear,
        )
        .sort(sortDateAscendingWithAccessors('awardedDate'))[0]?.awardedDate,
    [solicitation.id],
  );

  return (
    <>
      <EditReopenPeriodModal
        isOpen={showEditReopenPeriodModal}
        currentPeriodData={currentPeriodModalData}
        onClose={toggleEditReopenPeriodModal}
        previousPeriodData={
          reopenPeriods[currentPeriodModalData?.index - 1] || null
        }
        nextPeriodData={
          reopenPeriods[currentPeriodModalData?.index + 1] || null
        }
        solicitationID={solicitationID}
        openPeriod={openPeriod}
        solicitationStatus={solicitation?.status}
      />

      <ConnectedDeleteReopenPeriodModal
        isOpen={showDeleteReopenPeriodModal}
        onComplete={refetchSolicitation}
        onClose={toggleDeleteReopenPeriodModal}
        currentPeriodData={currentPeriodModalData}
      />

      <SolicitationNotification isModal={false} />
      <InactiveSIAlert
        solicitationPrograms={solicitation.programs}
        contractYear={solicitation.contractYear}
      />
      <SolicitationHeader
        solicitationNumber={solicitation?.solicitationNumber}
        solicitationStatus={solicitation?.status}
        solicitationType={solicitation?.solicitationType}
      />
      <SolicitationStepper currentStep={3} solicitation={solicitation} />
      <form data-testId="reopen-period-form">
        {!addPeriodLoading && (
          <SolicitationPeriod
            title="(Optional) Enter your reopen periods or mid-cycle periods. These dates cannot overlap with your open period, or each other."
            contractYear={solicitation.contractYear}
            formRegister={register}
            formControl={control}
            formWatch={watch}
            formErrors={errors}
            formValidationTrigger={trigger}
            minDate={newReopenPeriodMinDateFormated}
            formWrapperClassName="padding-3"
            {...reopenPeriodFieldProps}
          >
            <Button
              label="Add period and save"
              leftIcon={{
                name: 'add',
              }}
              onClick={handleSubmit(submitHandler)}
              variant="primary"
              className="width-card-lg"
            />
          </SolicitationPeriod>
        )}
      </form>
      <ReopenPeriodsTable
        data={addPeriodLoading ? [] : reopenPeriods}
        loading={addPeriodLoading}
        handleRowActionEdit={handleRowActionEdit}
        handleRowActionDelete={handleRowActionDelete}
        contractAwardDate={getContractAwardDate()}
      />
      <div className="grid-row padding-top-6">
        <Button
          label="Previous"
          data-testid="reopen-period-previous-button"
          leftIcon={{
            name: 'arrow_back',
          }}
          onClick={onClickPrev}
          variant="outline"
        />
        <Button
          label="Next"
          data-testid="reopen-period-next-button"
          rightIcon={{
            name: 'arrow_forward',
          }}
          className="margin-left-1"
          onClick={onClickNext}
          variant="outline"
        />
      </div>
    </>
  );
};

export default ReopenPeriods;
